import type { ReserveSnapshot } from '../../shared/types';
import type { UserReserve, UserSnapshot } from '../../shared/reader';

export type ActionKind = 'supply' | 'withdraw' | 'borrow' | 'repay';
export const MAX_UINT = (1n << 256n) - 1n;
/** Health factor kept after a "max" withdrawal, so that the transaction is not rejected by a price tick. */
const SAFE_HF = 1.02;
/** Share of the borrowing power a "max" borrow uses. */
const BORROW_HEADROOM = 0.99;

export interface Position {
  /** USD, floats are fine for display and previews: every binding check happens on-chain. */
  collateralUsd: number;
  debtUsd: number;
  /** Weighted average max LTV / liquidation threshold of the enabled collateral, as fractions. */
  maxLtv: number;
  liqThreshold: number;
  healthFactor: number; // Infinity without debt
  currentLtv: number;
  availableBorrowsUsd: number;
}

export function positionOf(u: UserSnapshot): Position {
  const collateralUsd = Number(u.totalCollateralBase) / 1e8;
  const debtUsd = Number(u.totalDebtBase) / 1e8;
  return derive(collateralUsd, debtUsd, u.ltvBps / 10_000, u.currentLiquidationThresholdBps / 10_000);
}

function derive(collateralUsd: number, debtUsd: number, maxLtv: number, liqThreshold: number): Position {
  return {
    collateralUsd, debtUsd, maxLtv, liqThreshold,
    healthFactor: debtUsd <= 1e-9 ? Number.POSITIVE_INFINITY : (collateralUsd * liqThreshold) / debtUsd,
    currentLtv: collateralUsd <= 0 ? 0 : debtUsd / collateralUsd,
    availableBorrowsUsd: Math.max(0, collateralUsd * maxLtv - debtUsd),
  };
}

const priceOf = (r: ReserveSnapshot): number => Number(r.priceUsd) / 1e8;
const tokens = (raw: bigint, decimals: number): number => Number(raw) / 10 ** decimals;

/** Whether an amount of this reserve counts (or will count, on a first deposit) towards the user's collateral. */
export function countsAsCollateral(r: ReserveSnapshot, ur: UserReserve | undefined): boolean {
  if (!r.usageAsCollateralEnabled || r.liquidationThresholdBps === 0) return false;
  if (ur && ur.supplied > 0n) return ur.usedAsCollateral;
  return r.ltvBps > 0; // the pool enables a first deposit as collateral only while its LTV is non-zero
}

/** Position after the action, without touching the chain. `amount` is raw token units. */
export function simulate(p: Position, r: ReserveSnapshot, ur: UserReserve | undefined, kind: ActionKind, amount: bigint): Position {
  const usd = tokens(amount, r.decimals) * priceOf(r);
  if (kind === 'borrow') return derive(p.collateralUsd, p.debtUsd + usd, p.maxLtv, p.liqThreshold);
  if (kind === 'repay') return derive(p.collateralUsd, Math.max(0, p.debtUsd - usd), p.maxLtv, p.liqThreshold);
  if (!countsAsCollateral(r, ur)) return p;
  const delta = kind === 'supply' ? usd : -Math.min(usd, p.collateralUsd);
  const c = p.collateralUsd + delta;
  if (c <= 1e-9) return derive(0, p.debtUsd, 0, 0);
  // weighted averages move with the mix of collateral
  const lt = (p.collateralUsd * p.liqThreshold + delta * (r.liquidationThresholdBps / 10_000)) / c;
  const ltv = (p.collateralUsd * p.maxLtv + delta * (r.ltvBps / 10_000)) / c;
  return derive(c, p.debtUsd, Math.max(0, ltv), Math.max(0, lt));
}

function capHeadroom(capWholeTokens: string, usedRaw: string, decimals: number): bigint | null {
  const cap = BigInt(capWholeTokens);
  if (cap === 0n) return null; // uncapped
  const room = cap * 10n ** BigInt(decimals) - BigInt(usedRaw);
  return room > 0n ? room : 0n;
}

const fromTokens = (v: number, decimals: number): bigint => {
  if (!(v > 0) || !Number.isFinite(v)) return 0n;
  // keep 8 significant decimals of the float, then scale up in bigint space
  const scaled = BigInt(Math.floor(v * 1e8));
  return decimals >= 8 ? scaled * 10n ** BigInt(decimals - 8) : scaled / 10n ** BigInt(8 - decimals);
};
const minBig = (...xs: bigint[]): bigint => xs.reduce((a, b) => (b < a ? b : a));

export interface MaxAmount {
  /** What the input shows. */
  amount: bigint;
  /** True when the transaction should send MAX_UINT so that no interest dust is left behind. */
  useMaxUint: boolean;
  /** Which limit produced the number, for the hint under the input. */
  limitedBy: 'wallet' | 'supplied' | 'debt' | 'health' | 'liquidity' | 'cap' | 'power';
}

export function maxFor(kind: ActionKind, p: Position, r: ReserveSnapshot, ur: UserReserve | undefined): MaxAmount {
  const wallet = ur?.walletBalance ?? 0n;
  const supplied = ur?.supplied ?? 0n;
  const debt = ur?.debt ?? 0n;
  const liquidity = BigInt(r.availableLiquidity);
  const price = priceOf(r);

  if (kind === 'supply') {
    const room = capHeadroom(r.supplyCap, r.totalSupplied, r.decimals);
    return room !== null && room < wallet ? { amount: room, useMaxUint: false, limitedBy: 'cap' } : { amount: wallet, useMaxUint: false, limitedBy: 'wallet' };
  }
  if (kind === 'repay') {
    return wallet >= debt ? { amount: debt, useMaxUint: true, limitedBy: 'debt' } : { amount: wallet, useMaxUint: false, limitedBy: 'wallet' };
  }
  if (kind === 'borrow') {
    const power = price > 0 ? fromTokens((p.availableBorrowsUsd * BORROW_HEADROOM) / price, r.decimals) : 0n;
    const room = capHeadroom(r.borrowCap, r.totalDebt, r.decimals);
    const amount = minBig(power, liquidity, room ?? power);
    return { amount, useMaxUint: false, limitedBy: amount === power ? 'power' : amount === liquidity ? 'liquidity' : 'cap' };
  }
  // withdraw
  let limit = supplied;
  let limitedBy: MaxAmount['limitedBy'] = 'supplied';
  if (p.debtUsd > 1e-9 && countsAsCollateral(r, ur) && price > 0) {
    // keep collateral x LT >= SAFE_HF x debt
    const spareUsd = (p.collateralUsd * p.liqThreshold - SAFE_HF * p.debtUsd) / (r.liquidationThresholdBps / 10_000);
    const byHealth = fromTokens(Math.max(0, spareUsd) / price, r.decimals);
    if (byHealth < limit) { limit = byHealth; limitedBy = 'health'; }
  }
  if (liquidity < limit) { limit = liquidity; limitedBy = 'liquidity'; }
  return { amount: limit, useMaxUint: limitedBy === 'supplied' && supplied > 0n, limitedBy };
}

export type RiskLevel = 'none' | 'good' | 'warning' | 'serious' | 'critical';
export function riskLevel(p: Position): RiskLevel {
  if (p.debtUsd <= 1e-9) return 'none';
  if (p.healthFactor < 1.1) return 'critical';
  if (p.currentLtv >= p.maxLtv) return 'serious'; // borrowing power exhausted
  if (p.healthFactor < 1.5) return 'warning';
  return 'good';
}

/** Aave's thresholds for colouring a health factor: green from 3, red below 1.1, orange in between. */
export const hfTone = (hf: number): 'good' | 'warning' | 'critical' => (hf >= 3 ? 'good' : hf < 1.1 ? 'critical' : 'warning');
export const hfText = (hf: number): string => (!Number.isFinite(hf) ? '∞' : hf > 100 ? '>100' : hf.toFixed(2));

/** How far the collateral can fall (all assets together) before the position becomes liquidatable. */
export const dropToLiquidation = (p: Position): number | null =>
  Number.isFinite(p.healthFactor) && p.healthFactor > 0 ? Math.max(0, 1 - 1 / p.healthFactor) : null;
